/**
 * 業務フロー図の線の引き方。`docs/web/spec-console-flow-drawing.md` の作図規約の実装である。
 *
 * 2026-09-21 の図（`d2a1aab38` の `user-story-flow-edges.ts`）の規則をそのまま移した。作り直しで
 * この規則を外したところ、線が節点を貫き、分岐の枝が重なって読めなくなったためである。
 *
 * 経路の決定をここ 1 箇所に置くのは、線の引き方が「エッジを組み立てる側」と「描く側」に
 * 分かれると、規約の一部だけが片方に実装されて図が場所ごとに違う引き方になるためである。
 * ここが返すのは SVG のパス文字列そのもので、描画側は受け取った文字列を引くだけにする。
 *
 * ただし**位置の割り当ては持たない**。縦線・水路・上がる位置は「同じ群の他の線がどこを通るか」
 * で決まるので、1 本を見て決められない。図 1 枚ぶんをまとめて解く責務は組み立て側
 * （`build.tsx`）にあり、ここは解かれた座標を受け取って形にする。
 *
 * React Flow の既定の `smoothstep` を使わないのは、あれが「出口から一定距離だけ進んでから
 * 曲がる」しか制御できず、規約 2（線は直線か水路経由の 2 形だけ）と規約 3（位置は区間で詰める）
 * を満たせないためである。分岐の複数本が同じ経路に重なるのも既定の挙動で、外から直せない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の線を、作図規約（直線か水路経由の 2 形・根元のずらし・区間で詰める位置）に沿った直交パスとして組み立てる。
 * @graph-connects none
 */
import {
  GUTTER_INSET,
  GUTTER_LINE_ZONE,
  GUTTER_RIGHT_RESERVE,
  GUTTER_STEP,
  LABEL_ZONE_WIDTH,
  LANE_HEIGHT,
  NODE_HEIGHT,
  NODE_OFFSET_Y,
} from './constants.js';

/**
 * 帯の中に線を等間隔で並べるときの刻み。
 *
 * 本数が少ないうちは既定の `GUTTER_STEP` を使い、増えたら帯からはみ出さないところまで詰める。
 * 「置ける本数に上限を設け、超えたら最後の位置へ重ねる」作りだと、上限を超えた瞬間に複数の線が
 * 同じ座標を走って 1 本に見える。実測では 12 本に分岐する図で縦線が 4 本重なった。本数に上限を
 * 置かず刻みのほうを詰めれば、何本来ても別々の位置に置ける。
 *
 * `usable` は帯のうち実際にずらせる幅で、両端に `GUTTER_INSET` ぶんの余白を残した残りを
 * 呼び出し側が渡す。帯の縁ちょうどまで使うと、隣の帯（条件ラベル）の端と線が接する。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 帯に n 本の線を収める刻みを、帯からはみ出さない範囲で決める。
 * @graph-connects none
 */
export function laneStep(usable: number, laneCount: number, preferred: number): number {
  if (laneCount <= 1) return preferred;
  return Math.min(preferred, usable / (laneCount - 1));
}

/** 条件ラベルの帯の左端（升目の右端からの距離）。縦線の帯の外側から始める。 @graph-connects none */
export const LABEL_ZONE_LEFT = GUTTER_LINE_ZONE + GUTTER_INSET;

/** 条件ラベルの中心（升目の右端からの距離）。 @graph-connects none */
export const GUTTER_LABEL_CENTER = LABEL_ZONE_LEFT + LABEL_ZONE_WIDTH / 2;

/** レーン帯の下端から水路の帯までの距離。 @graph-connects none */
export const BYPASS_INSET_Y = 12;

/** 水路を 1 本ずつずらす量。 @graph-connects none */
export const CHANNEL_STEP = 6;

/** ノードの下端と水路の帯のあいだに残す余白。 @graph-connects none */
export const CHANNEL_MARGIN = 24;

/**
 * 1 つの段で水路に使える高さ。
 *
 * 段の高さから、ノードが使う高さ（`NODE_OFFSET_Y` + `NODE_HEIGHT`）と上下の余白を引いた残りである。
 * ここに何本の水路が来ても、`laneStep` が刻みを詰めて収める。
 * @graph-connects none
 */
export const CHANNEL_ZONE =
  LANE_HEIGHT - NODE_OFFSET_Y - NODE_HEIGHT - BYPASS_INSET_Y - CHANNEL_MARGIN;

/** 同じノードから出る線を、根元で縦にずらす量。 @graph-connects none */
export const DEPARTURE_STEP = 12;

/** 根元のずらし幅の上限。ノードの高さの内側に収める。 @graph-connects none */
export const MAX_DEPARTURE_SPREAD = 48;

/** 線を引くのに要る 1 本ぶんの情報。座標はすべて図の絶対位置で受け取る。 */
export interface FlowEdgeGeometry {
  /**
   * 始点の図形の右端 X。線はここから出る。
   *
   * 升目の右端ではないのは、菱形や錠剤形が升目より狭く、升目の右端から出すと線が図形から
   * 離れた空中から生えて見えるためである。
   */
  startX: number;
  /** 終点の図形の左端 X。線はここへ入る。 */
  endX: number;
  /** 始点ノードの上端 Y。 */
  sourceY: number;
  /** 終点ノードの上端 Y。 */
  targetY: number;
  /** 終点が始点の隣の列か。隣なら同じ高さのとき直線で引く。 */
  adjacent: boolean;
  /** 始点ノードから出る線のうち何本目か（0 始まり）。 */
  order: number;
  /**
   * 始点ノードから出る線の総数。
   *
   * 根元をずらす量を決めるのに使う。1 本だけのときはずらさない。ずらすと、同じ高さの隣へ行く
   * 線が斜めに見える形になり、「何かを避けている」意味が乗る。
   */
  outgoingTotal: number;
  /** 始点側のガターで縦に降りる X。重ならないよう呼び出し側が割り当てる。 */
  turnX: number;
  /** 横に走る水路の Y。重ならないよう呼び出し側が割り当てる。 */
  runY: number;
  /** 終点の手前で縦に上がる X。重ならないよう呼び出し側が割り当てる。 */
  riseX: number;
}

/** 1 本の線が占める区間。重なりを避けて割り当てるための入力。 */
export interface LaneInterval {
  /** 区間の始まり。 */
  from: number;
  /** 区間の終わり。 */
  to: number;
}

/**
 * 重ならない区間どうしが同じ位置を共有できるように、位置番号を割り当てる。
 *
 * 出てくる順に番号を振るだけだと、離れた場所を走る線まで別々の位置を取り、位置の数が無駄に
 * 増える。区間が重なっていない線は同じ位置を使ってよいので、空いている最小の番号を探して詰める。
 *
 * 番号の上限は設けない。上限を設けて超えた分を最後の番号へ重ねると、そこがそのまま「線が
 * 重なって見える」箇所になる。何本必要かは呼び出し側が受け取り、帯に収まる刻み（`laneStep`）へ
 * 変換する。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 重ならない区間が同じ位置を共有するように、線の位置番号を割り当てる。
 * @graph-connects none
 */
export function packLanes(intervals: readonly LaneInterval[]): number[] {
  const used: LaneInterval[][] = [];

  return intervals.map((interval) => {
    for (const [lane, taken] of used.entries()) {
      const hits = taken.some(
        (other) => Math.min(other.to, interval.to) - Math.max(other.from, interval.from) > 0,
      );
      if (!hits) {
        taken.push(interval);
        return lane;
      }
    }
    used.push([interval]);
    return used.length - 1;
  });
}

/** ノードの縦の中心 Y（線の出入口）。 @graph-connects none */
function nodeCenterY(topY: number): number {
  return topY + NODE_HEIGHT / 2;
}

/**
 * 同じノードから出る線の根元の Y。
 *
 * 中心から上下へ均等にずらす。全部を中心から出すと、根元からガターへ向かう水平区間が完全に
 * 重なり、分岐から何本出ているのかが図から読めない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 同じノードから出る線の根元を、本数に応じて縦にずらす。
 * @graph-connects none
 */
export function departureY(centerY: number, order: number, total: number): number {
  if (total <= 1) return centerY;
  const step = Math.min(DEPARTURE_STEP, MAX_DEPARTURE_SPREAD / (total - 1));
  return centerY - (step * (total - 1)) / 2 + step * order;
}

/**
 * 縦線を置くガター内の X 座標。
 *
 * 分岐の何本目かで刻みぶんずらす（規約 3）。刻みは既定で `GUTTER_STEP` だが、その列に必要な
 * 本数が増えたら縦線の帯に収まるところまで詰める（`laneStep`）。ずらし続けてガターをはみ出すと、
 * 規約 2（縦線はガター内）を破ってノードを横切るほうが読みにくくなるためである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分岐の N 本目の縦線を置くガター内の X 座標を決める。
 * @graph-connects laneStep [calls] 本数に応じた刻みの決定
 */
export function gutterX(cellRight: number, order: number, laneCount: number): number {
  const step = laneStep(GUTTER_LINE_ZONE - GUTTER_INSET * 2, laneCount, GUTTER_STEP);
  return cellRight + GUTTER_INSET + step * order;
}

/**
 * 線が横に走るときに使う水路の Y 座標。
 *
 * 段（ノード 1 行分）の下側の帯に、`CHANNEL_STEP` ずつ間を空けて何本も引く。1 段に 1 本しか
 * 持たないと、その段を横切る線が全部同じ高さを走って 1 本に見える。どのノードからどのノードへ
 * 繋がっているのかが読めなくなるので、線ごとに別の高さを与える。
 *
 * 基準はノードの上端から測る。帯ではなく**段**を基準にするのは、段が増えたレーンで下の段の
 * 線が上の段の帯の下へ集まってしまうのを避けるためである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 線が横に走る水路の Y 座標を、段の下側の帯の中で番号ぶんずらして決める。
 * @graph-connects laneStep [calls] 本数に応じた刻みの決定
 */
export function channelY(sourceTopY: number, channel: number, channelCount: number): number {
  const rowTop = sourceTopY - NODE_OFFSET_Y;
  const base = rowTop + LANE_HEIGHT - BYPASS_INSET_Y;
  return base - laneStep(CHANNEL_ZONE, channelCount, CHANNEL_STEP) * channel;
}

/**
 * 終点の手前で線が上がり直す X 座標。
 *
 * 終点の**升目の左端**から `GUTTER_INSET` 離した位置を基準に、入ってくる順で左へずらす。升目の
 * 内側で上げないのは、分岐の名前が図形の下・升目の幅いっぱいに置かれており、升目の中を縦に
 * 走ると名前を貫通するためである。ずらす幅はガター右端の予約（`GUTTER_RIGHT_RESERVE`）に収め、
 * 条件ラベルの帯へ食い込ませない。本数が増えたら刻みを詰めて、この予約の中に収める。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 終点の手前で線が上がり直す X 座標を、入ってくる順にずらして決める。
 * @graph-connects laneStep [calls] 本数に応じた刻みの決定
 */
export function entryX(targetCellLeft: number, entryOrder: number, laneCount: number): number {
  const step = laneStep(GUTTER_RIGHT_RESERVE - GUTTER_INSET * 2, laneCount, GUTTER_STEP);
  return targetCellLeft - GUTTER_INSET - step * entryOrder;
}

/**
 * 線 1 本の直交パスを組み立てる。
 *
 * 形は 2 つしかない。
 *
 * 1. **直線** — 同じ高さで隣の列へ行くとき（規約 4）。曲げると「何かを避けている」意味が乗る
 * 2. **水路経由** — それ以外すべて。始点側のガターで縦に降り、自分の水路を横に走り、終点の
 *    手前のガターで縦に上がって入る（規約 5）
 *
 * 隣の列へ行くだけの線も水路を通すのは、終点の行を長く横に走らせないためである。終点の行を
 * 走らせると、同じノードへ合流する線どうしがその行で重なり、何本が合流したのかが読めない。
 * 水路は線ごとに高さが違い、終点の手前で上がる位置も入ってくる順で違うので、重なるのは終点の
 * すぐ手前だけになる。
 *
 * 戻る向き（終点が始点より左）でも同じ形で引ける。水路の番号が別なら、進む線と戻る線が同じ
 * 高さを走ることもない。
 *
 * 戻り値を SVG のパス文字列にするのは、描画側が経路を再計算できないようにするためである。
 * 座標の配列を返すと、描く側が「角を丸める」「途中で折る」といった判断を持ててしまい、
 * 規約の実装が 2 箇所に分かれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 線 1 本の直交パスを、直線か水路経由のどちらかとして組み立てる。
 * @graph-connects departureY [calls] 同じノードから出る線の根元の高さ
 * @graph-connects buildOrthogonalPath [calls] 端点を解いた座標からの直交パス
 */
export function buildEdgePath(geometry: FlowEdgeGeometry): string {
  return buildOrthogonalPath({
    startX: geometry.startX,
    startY: departureY(nodeCenterY(geometry.sourceY), geometry.order, geometry.outgoingTotal),
    endX: geometry.endX,
    endY: nodeCenterY(geometry.targetY),
    adjacent: geometry.adjacent,
    turnX: geometry.turnX,
    runY: geometry.runY,
    riseX: geometry.riseX,
  });
}

/**
 * 端点の高さまで解いた線 1 本の情報。寸法（箱の高さ・段の高さ）を持たない。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 直交パスを組み立てるのに要る、端点・縦に降りる X・水路の Y・上がる X を表す。
 * @graph-connects none
 */
export interface OrthogonalPathGeometry {
  /** 始点の図形の縁の X。 */
  readonly startX: number;
  /** 始点の根元の Y（`departureY` でずらした後の値）。 */
  readonly startY: number;
  /** 終点の図形の縁の X。 */
  readonly endX: number;
  /** 終点へ入る Y。 */
  readonly endY: number;
  /** 終点が始点の隣の列か。 */
  readonly adjacent: boolean;
  /** 始点側のガターで縦に降りる X。 */
  readonly turnX: number;
  /** 横に走る水路の Y。 */
  readonly runY: number;
  /** 終点の手前で縦に上がる X。 */
  readonly riseX: number;
}

/**
 * 線 1 本の直交パスを、端点の高さまで解いた座標から組み立てる（規約 2 の 2 形）。
 *
 * 業務フロー図と業務のロジック図が同じ形で線を引くための口である。寸法は図ごとに違うので
 * （作図規約「寸法はエンジンの中に持ち、業務ルール側の図とは共有しない」）、端点の高さと位置の
 * 割り当てを解くのは各図の組み立て側に任せ、ここは形だけを決める。
 *
 * @param geometry 端点と割り当て済みの位置
 * @returns SVG のパス文字列
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 端点の高さまで解いた座標から、線 1 本を直線か水路経由の直交パスとして組み立てる。
 * @graph-connects none
 */
export function buildOrthogonalPath(geometry: OrthogonalPathGeometry): string {
  const { startX, startY, endX, endY } = geometry;

  /* 形 1: 同じ高さで隣の列。曲げない。 */
  if (geometry.adjacent && startY === endY) {
    return `M ${String(startX)} ${String(startY)} L ${String(endX)} ${String(endY)}`;
  }

  return [
    `M ${String(startX)} ${String(startY)}`,
    `L ${String(geometry.turnX)} ${String(startY)}`,
    `L ${String(geometry.turnX)} ${String(geometry.runY)}`,
    `L ${String(geometry.riseX)} ${String(geometry.runY)}`,
    `L ${String(geometry.riseX)} ${String(endY)}`,
    `L ${String(endX)} ${String(endY)}`,
  ].join(' ');
}

/**
 * 条件ラベルを置く位置の初期値。
 *
 * X は**ラベルの帯の中心**に固定する（規約 6）。縦線の帯へ置くと、分岐から出た複数本の縦線を
 * 不透明なラベルが覆い、どの線がどの枝かを追えなくなる。Y は始点の高さから少し上へ外し、
 * 分岐から出た直後にどの条件の枝かが読めるようにする。
 *
 * ここで返すのは初期値であり、ラベルどうし・ラベルと水平線の衝突は
 * `resolveEdgeLabelPositions` が縦へ逃がして解く。1 本だけを見て衝突は解けない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 条件ラベルの初期位置を、ガターのラベル帯の中心に決める。
 * @graph-connects departureY [calls] 同じノードから出る線の根元の高さ
 */
export function edgeLabelPosition(geometry: FlowEdgeGeometry & { sourceCellRight: number }): {
  x: number;
  y: number;
} {
  return {
    x: geometry.sourceCellRight + GUTTER_LABEL_CENTER,
    y:
      departureY(nodeCenterY(geometry.sourceY), geometry.order, geometry.outgoingTotal) -
      NODE_OFFSET_Y / 4,
  };
}

/** ラベル 1 行ぶんの高さ（px）。`FlowGutterEdge` の `text-xs` の行送りと揃える。 @graph-connects none */
export const LABEL_LINE_HEIGHT = 16;

/** ラベルの上下の内側余白の合計（px）。 @graph-connects none */
export const LABEL_PADDING_Y = 6;

/** 積んだラベルどうしのあいだに残す隙間（px）。 @graph-connects none */
export const LABEL_GAP = 8;

/**
 * ラベルの文字が使える幅（px）。
 *
 * 左右の内側余白と枠線を引いた実寸より、さらに少し狭く見る。見積りは**足りない側に外すと
 * ラベルどうしの重なりになり、多い側に外しても隙間が増えるだけ**である。実測（内容幅 119px）に
 * 対して式が 6 行と 7 行の境目に乗っており、字形の差で簡単に足りない側へ倒れる。
 * @graph-connects none
 */
export const LABEL_TEXT_WIDTH = LABEL_ZONE_WIDTH - 20;

/** 条件ラベルの文字の大きさ（px）。`FlowGutterEdge` の `text-xs` と揃える。 @graph-connects none */
export const LABEL_FONT_SIZE = 12;

/** 半角 1 文字の幅が文字の大きさに占める割合。 @graph-connects none */
const HALF_WIDTH_RATIO = 0.62;

/** 折り返しの単位。英数字の連なりは語の途中で折らないので 1 つの塊として扱う。 @graph-connects none */
const WRAP_TOKENS = /[A-Za-z0-9]+|[\s\S]/g;

/** 折り返し単位 1 つの幅（px）。全角は文字の大きさそのまま、半角はその割合で見積もる。 @graph-connects none */
function tokenWidth(token: string, fontSize: number): number {
  let width = 0;
  for (const char of token) {
    width += /[ -~｡-ﾟ]/.test(char) ? fontSize * HALF_WIDTH_RATIO : fontSize;
  }
  return width;
}

/**
 * 折り返したときの行数を見積もる。
 *
 * 1 文字ずつ幅を足すのではなく、**英数字の連なりを 1 つの塊**として詰める。ブラウザは語の
 * 途中では折らないので、1 文字単位で数えると `verificationLink` のような語の手前に生じる
 * 余りが行数から漏れ、見積りが実際より小さくなる。小さい見積りはラベルどうしの重なりとして
 * 現れる。
 *
 * DOM を測ってから動かす方法は採らない。測るには一度描く必要があり、描いた直後の 1 フレームは
 * 必ず重なった状態で表示される。動かす前の状態がユーザーに見えるなら「重なりが無い」とは
 * 言えない。純関数で先に決めれば、最初の描画から重なりが無い。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 文字列を指定幅で折り返したときの行数を見積もる。
 * @graph-connects none
 */
export function estimateLineCount(text: string, width: number, fontSize: number): number {
  let lines = 1;
  let used = 0;
  for (const token of text.match(WRAP_TOKENS) ?? []) {
    const size = tokenWidth(token, fontSize);
    if (used > 0 && used + size > width) {
      lines += 1;
      used = 0;
    }
    if (size > width) {
      /* 1 行に収まらない語は途中で折られる。折れた回数ぶん行が増える。 */
      const wrapped = Math.ceil(size / width) - 1;
      lines += wrapped;
      used = size - wrapped * width;
      continue;
    }
    used += size;
  }
  return lines;
}

/**
 * 折り返したラベルの高さを見積もる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 条件ラベルの折り返し後の高さを見積もる。
 * @graph-connects estimateLineCount [calls] 折り返し後の行数
 */
export function estimateLabelHeight(text: string): number {
  return (
    estimateLineCount(text, LABEL_TEXT_WIDTH, LABEL_FONT_SIZE) * LABEL_LINE_HEIGHT + LABEL_PADDING_Y
  );
}

/** 線が水平に走る区間 1 つ。ラベルが乗ると線が隠れる。 */
interface HorizontalRun {
  /** 走る高さ。 */
  y: number;
  /** 左端 X。 */
  from: number;
  /** 右端 X。 */
  to: number;
}

/**
 * パス文字列から、線が水平に走る区間を取り出す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 直交パスから、線が水平に走る区間を取り出す。
 * @graph-connects none
 */
export function horizontalRunsOf(paths: readonly string[]): HorizontalRun[] {
  const runs: HorizontalRun[] = [];
  for (const path of paths) {
    const points = [...path.matchAll(/[ML] (-?[\d.]+) (-?[\d.]+)/g)].map((match) => ({
      x: Number(match[1]),
      y: Number(match[2]),
    }));
    for (let i = 0; i + 1 < points.length; i += 1) {
      const a = points[i];
      const b = points[i + 1];
      if (a.y !== b.y) continue;
      runs.push({ y: a.y, from: Math.min(a.x, b.x), to: Math.max(a.x, b.x) });
    }
  }
  return runs;
}

/** ラベルが避ける相手の矩形（ノードが占める面）。 */
export interface LabelObstacle {
  /** 左端 X。 */
  left: number;
  /** 右端 X。 */
  right: number;
  /** 上端 Y。 */
  top: number;
  /** 下端 Y。 */
  bottom: number;
}

/** 位置を解いた条件ラベル 1 枚。 */
export interface ResolvedEdgeLabel {
  /** 対応する遷移の ID。 */
  id: string;
  /** ラベルの中心 X。 */
  x: number;
  /** ラベルの中心 Y。 */
  y: number;
}

/** `resolveEdgeLabelPositions` の入力 1 件。 */
export interface EdgeLabelPlacement {
  /** 対応する遷移の ID。 */
  id: string;
  /** ラベルの本文。高さの見積りに使う。 */
  label: string;
  /** 初期の中心 X。 */
  x: number;
  /** 初期の中心 Y。 */
  y: number;
}

/**
 * 条件ラベルを、ラベルどうし・ノード・水平な線のどれにも重ならない位置へ解く。
 *
 * 上から順に確定させ、当たったものだけを下へ逃がす。横へは逃がさない。横へずらすとガターを
 * 出て隣の列のノードへ掛かり、逃がす方向が一意に決まらなくなる。縦は帯の中に空きがあるので、
 * 下へ送るだけで必ず解ける。
 *
 * 1 本ずつ独立に決められないのは、衝突が「他のラベルとノードがどこに居るか」で決まるため
 * である。図 1 枚ぶんをまとめて受け取る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 条件ラベルを、ラベルどうし・ノード・水平な線のどれにも重ならない縦位置へ解く。
 * @graph-connects estimateLabelHeight [calls] 折り返し後のラベル高さの見積り
 * @graph-connects horizontalRunsOf [calls] 水平区間の取り出し
 */
export function resolveEdgeLabelPositions(
  placements: readonly EdgeLabelPlacement[],
  paths: readonly string[],
  obstacles: readonly LabelObstacle[],
): ResolvedEdgeLabel[] {
  const runs = horizontalRunsOf(paths);
  const ordered = [...placements].sort((left, right) => left.y - right.y || left.x - right.x);
  const placed: (ResolvedEdgeLabel & { height: number })[] = [];

  for (const entry of ordered) {
    const height = estimateLabelHeight(entry.label);
    const left = entry.x - LABEL_ZONE_WIDTH / 2;
    const right = entry.x + LABEL_ZONE_WIDTH / 2;
    let y = entry.y;

    /* 逃がす回数は有限。総当たりの上限を置いて、解けないときも必ず止める。 */
    for (let guard = 0; guard < ordered.length + runs.length + obstacles.length + 1; guard += 1) {
      const hit = placed.find(
        (other) =>
          Math.abs(other.x - entry.x) < LABEL_ZONE_WIDTH &&
          Math.abs(other.y - y) < (other.height + height) / 2 + LABEL_GAP,
      );
      if (hit !== undefined) {
        y = hit.y + (hit.height + height) / 2 + LABEL_GAP;
        continue;
      }
      const box = obstacles.find(
        (candidate) =>
          candidate.left < right &&
          candidate.right > left &&
          candidate.top < y + height / 2 + LABEL_GAP &&
          candidate.bottom > y - height / 2 - LABEL_GAP,
      );
      if (box !== undefined) {
        y = box.bottom + height / 2 + LABEL_GAP;
        continue;
      }
      const run = runs.find(
        (candidate) =>
          candidate.from < right &&
          candidate.to > left &&
          Math.abs(candidate.y - y) < height / 2 + LABEL_GAP,
      );
      if (run === undefined) break;
      y = run.y + height / 2 + LABEL_GAP;
    }

    placed.push({ id: entry.id, x: entry.x, y, height });
  }

  return placed.map(({ id, x, y }) => ({ id, x, y }));
}
